import React, { useState } from "react";
import { Box, Button, Flex, FormControl, Input, Text, Textarea } from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AppBar } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { PRIMARY, BORDER, TEXT } from "../../components/requestUi";
import ImageUploader from "../../components/ImageUploader";
import { createMakeupWorkApi } from "../../api/requests.api";
import { extractErrorMessage } from "../../api/client";
import { dayKey } from "../../utils/format";
import { EMP_BASE_PATH } from "../../config";

const BLUE_GRADIENT = "linear(to-r, #68A2FF, #2168DA)";

const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="8px" color={TEXT}>
    {children}
    {required && <Text as="span" color="#EF2E14"> *</Text>}
  </Text>
);

const MakeupCreate = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { dayLeave: dayKey(), note: "" } });

  const createMutation = useMutation({
    mutationFn: createMakeupWorkApi,
    onSuccess: () => {
      toast.success("Tạo đăng ký làm bù thành công");
      queryClient.invalidateQueries({ queryKey: ["emp", "makeup-list"] });
      navigate(`${EMP_BASE_PATH}/lam-bu`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Tạo đơn thất bại")),
  });

  const onSubmit = (v) => {
    if (images.length === 0) {
      toast.error("Ảnh minh chứng là trường bắt buộc");
      return;
    }
    createMutation.mutate({ dayLeave: v.dayLeave, note: v.note.trim(), images });
  };

  const formEl = (
      <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <FormControl mb="18px" isInvalid={!!errors.dayLeave}>
          <Label required>Ngày làm bù</Label>
          <Input
            type="date"
            borderColor={BORDER}
            borderRadius="8px"
            h="48px"
            fontSize="14px"
            {...register("dayLeave", { required: "Vui lòng chọn ngày" })}
          />
          {errors.dayLeave && (
            <Text fontSize="12px" color="red.500" mt="4px">{errors.dayLeave.message}</Text>
          )}
        </FormControl>

        <FormControl mb="18px">
          <Label required>Nội dung</Label>
          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <Textarea {...field} placeholder="Điền nội dung làm bù" borderColor={BORDER} borderRadius="8px" rows={4} fontSize="14px" />
            )}
          />
        </FormControl>

        <FormControl mb="20px">
          <Label required>Ảnh minh chứng</Label>
          <ImageUploader value={images} onChange={setImages} max={5} />
        </FormControl>

        <Flex gap="10px" mt="auto" pt="16px">
          <Button
            flex="1"
            h="48px"
            variant="outline"
            borderColor={PRIMARY}
            color={PRIMARY}
            borderRadius="10px"
            _hover={{ bg: "#EAF2FF" }}
            onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu`)}
          >
            Hủy
          </Button>
          <Button
            flex="1"
            h="48px"
            type="submit"
            isLoading={createMutation.isPending}
            bgGradient={BLUE_GRADIENT}
            color="white"
            borderRadius="10px"
            _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
          >
            Thêm
          </Button>
        </Flex>
      </form>
  );

  // ===================== Bản WEB (desktop) =====================
  // Giữ nguyên form/validate/mutation, chỉ đặt trong khung trang kiểu admin.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Đăng ký làm bù"
        right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/lam-bu`)} />}
      >
        <DesktopPanel p="24px">
          {formEl}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Đăng ký làm bù" onBack={() => navigate(`${EMP_BASE_PATH}/lam-bu`)} />

      <Box px="16px" pt="16px" pb="16px" flex="1" display="flex" flexDirection="column">
        {formEl}
      </Box>
    </Box>
  );
};

export default MakeupCreate;
